import React from 'react';
import { useTranslation } from 'react-i18next';
import './NotificationsPagination.css';

function buildPaginationItems(currentPage, totalPages) {
  if (totalPages <= 9) {
    return Array.from({ length: totalPages }, (_, idx) => idx + 1);
  }

  const first = [1, 2, 3];
  const middleStart = Math.max(1, Math.min(currentPage - 1, totalPages - 2));
  const middle = [middleStart, middleStart + 1, middleStart + 2].filter(
    (p) => p >= 1 && p <= totalPages
  );
  const last = [totalPages - 2, totalPages - 1, totalPages];

  const uniqueSorted = [...new Set([...first, ...middle, ...last])]
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b);

  const items = [];
  for (let i = 0; i < uniqueSorted.length; i += 1) {
    const page = uniqueSorted[i];
    const prev = uniqueSorted[i - 1];
    if (i > 0 && page - prev > 1) {
      items.push('ellipsis');
    }
    items.push(page);
  }

  return items;
}

function NotificationsPagination({ 
  currentPage, 
  totalPages, 
  totalCount, 
  loading, 
  onPageChange 
}) {
  const { t } = useTranslation('notifications');

  if (totalPages <= 1) {
    return null;
  }

  const pageItems = buildPaginationItems(currentPage, totalPages);

  return (
    <div className="notifications-pagination">
      <button
        className="pagination-btn"
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1 || loading}
      >
        ← {t('notifications.previous') || 'Poprzednia'}
      </button>
      
      <div className="pagination-info">
        <span>
          {t('notifications.page') || 'Strona'} {currentPage} {t('notifications.of') || 'z'} {totalPages}
        </span>
        {totalCount > 0 && (
          <span className="pagination-total">
            ({totalCount} {t('notifications.total') || 'łącznie'})
          </span>
        )}
      </div>

      <div className="pagination-pages" aria-label="Pagination pages">
        {pageItems.map((item, idx) => {
          if (item === 'ellipsis') {
            return (
              <span key={`ellipsis-${idx}`} className="pagination-ellipsis" aria-hidden>
                …
              </span>
            );
          }

          return (
            <button
              key={`page-${item}`}
              className={`pagination-page-btn ${item === currentPage ? 'active' : ''}`}
              onClick={() => onPageChange(item)}
              disabled={loading || item === currentPage}
              aria-current={item === currentPage ? 'page' : undefined}
            >
              {item}
            </button>
          );
        })}
      </div>
      
      <button
        className="pagination-btn"
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages || loading}
      >
        {t('notifications.next') || 'Następna'} →
      </button>
    </div>
  );
}

export default NotificationsPagination;
